import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { toastSuccess, toastError } from '../ui/toast';
import { colors as theme } from '../theme';

const colors = {
  bg: theme.bg, card: theme.surface1, border: theme.borderStrong,
  text: theme.text, muted: theme.textMuted, green: '#4A8F62',
  greenBg: 'rgba(74,143,98,0.12)', error: theme.danger,
};

const TIER_META = {
  free:    { label: 'Free',    color: theme.textMuted, bg: 'rgba(142,142,147,0.12)' },
  monthly: { label: 'Monthly', color: '#4A8F62', bg: 'rgba(74,143,98,0.12)'  },
  bundle:  { label: 'Bundle',  color: '#8B72D0', bg: 'rgba(139,114,208,0.12)' },
};

function TierBadge({ tier }) {
  const m = TIER_META[tier] ?? TIER_META.free;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
      color: m.color, backgroundColor: m.bg, border: `1px solid ${m.color}40`,
      textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>
      {m.label}
    </span>
  );
}

export default function CipherNutritionPanel({ onBack }) {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('all'); // all | free | monthly | bundle
  const [acting, setActing]       = useState(null);  // userId being acted on

  const getAuthHeader = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session ? `Bearer ${session.access_token}` : null;
  };

  const callApi = async (body) => {
    const authHeader = await getAuthHeader();
    const res = await fetch('/api/nutrition-admin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: authHeader },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  };

  useEffect(() => {
    callApi({ action: 'list' })
      .then(data => setUsers(data.users ?? []))
      .catch(err => toastError(err.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGrant = async (user, tier) => {
    setActing(user.id);
    try {
      await callApi({ action: 'grant', email: user.email, tier });
      toastSuccess(`${TIER_META[tier].label} granted to ${user.email}`);
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, tier, status: 'active' } : u));
    } catch (err) {
      toastError(err.message);
    } finally {
      setActing(null);
    }
  };

  const handleRevoke = async (user) => {
    setActing(user.id);
    try {
      await callApi({ action: 'revoke', email: user.email });
      toastSuccess(`Access revoked for ${user.email}`);
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, tier: 'free', status: null } : u));
    } catch (err) {
      toastError(err.message);
    } finally {
      setActing(null);
    }
  };

  const filtered = users.filter(u => {
    const matchesSearch = !search || u.email?.toLowerCase().includes(search.toLowerCase()) || u.name?.toLowerCase().includes(search.toLowerCase());
    const matchesTier   = filter === 'all' || u.tier === filter;
    return matchesSearch && matchesTier;
  });

  const counts = { all: users.length, free: 0, monthly: 0, bundle: 0 };
  for (const u of users) { if (counts[u.tier] !== undefined) counts[u.tier]++; }

  return (
    <div style={{ padding: '24px 20px', backgroundColor: colors.bg, minHeight: '100vh', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif" }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: colors.muted, cursor: 'pointer', fontSize: '14px', padding: 0 }}>
          ← Back
        </button>
        <div>
          <h2 style={{ color: colors.text, fontSize: '20px', fontWeight: 700, margin: 0 }}>Cipher Nutrition</h2>
          <p style={{ color: colors.muted, fontSize: '13px', margin: '2px 0 0' }}>
            {loading ? 'Loading…' : `${users.length} user${users.length !== 1 ? 's' : ''}`}
          </p>
        </div>
      </div>

      {/* Tier filter pills + search */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'center' }}>
        {['all', 'monthly', 'bundle', 'free'].map(t => (
          <button key={t} onClick={() => setFilter(t)} style={{
            padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
            cursor: 'pointer', border: '1px solid',
            borderColor: filter === t ? (TIER_META[t]?.color ?? colors.green) : colors.border,
            backgroundColor: filter === t ? (TIER_META[t]?.bg ?? colors.greenBg) : 'transparent',
            color: filter === t ? (TIER_META[t]?.color ?? colors.green) : colors.muted,
          }}>
            {t === 'all' ? 'All' : TIER_META[t].label} ({counts[t]})
          </button>
        ))}
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search name or email…"
          style={{
            marginLeft: 'auto', padding: '7px 12px', backgroundColor: colors.card,
            border: `1px solid ${colors.border}`, borderRadius: '8px',
            color: colors.text, fontSize: '13px', outline: 'none', minWidth: '220px',
          }}
        />
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ color: colors.muted, textAlign: 'center', padding: '60px 0' }}>Loading users…</div>
      ) : filtered.length === 0 ? (
        <div style={{ color: colors.muted, textAlign: 'center', padding: '60px 0' }}>No users found</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filtered.map(u => {
            const isActing = acting === u.id;
            return (
              <div key={u.id} style={{
                display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
                padding: '14px 16px', backgroundColor: colors.card,
                border: `1px solid ${colors.border}`, borderRadius: '14px',
              }}>
                {/* User info */}
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ color: colors.text, fontWeight: 600, fontSize: '14px' }}>
                    {u.name ?? '—'}
                  </div>
                  <div style={{ color: colors.muted, fontSize: '12px', marginTop: '2px' }}>{u.email}</div>
                </div>

                {/* Tier badge */}
                <TierBadge tier={u.tier} />

                {/* Access until */}
                <div style={{ color: colors.muted, fontSize: '12px', minWidth: '100px' }}>
                  {u.access_until
                    ? `Until ${new Date(u.access_until).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                    : u.tier === 'free' ? '—' : 'No expiry set'}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  {u.tier !== 'monthly' && (
                    <button onClick={() => handleGrant(u, 'monthly')} disabled={isActing} style={btnStyle(colors.green, isActing)}>
                      {isActing ? '…' : 'Monthly'}
                    </button>
                  )}
                  {u.tier !== 'bundle' && (
                    <button onClick={() => handleGrant(u, 'bundle')} disabled={isActing} style={btnStyle('#8B72D0', isActing)}>
                      {isActing ? '…' : 'Bundle'}
                    </button>
                  )}
                  {u.tier !== 'free' && (
                    <button onClick={() => handleRevoke(u)} disabled={isActing} style={btnStyle(colors.error, isActing)}>
                      {isActing ? '…' : 'Revoke'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const btnStyle = (color, disabled) => ({
  padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
  backgroundColor: `${color}15`, border: `1px solid ${color}50`, color,
});
